
// client/src/components/events/EventItemDetail.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import ImageUploadModal from "../media/ImageUploadModal";
import NativeCameraCapture from "../media/NativeCameraCapture";
import AppFooter from "../AppFooter";
import { FaCamera, FaUpload, FaPlus, FaShare, FaArrowLeft, FaTrash, FaSave, FaCloudUploadAlt, FaTh, FaList } from "react-icons/fa";
import CommentsPanel from "../comments/CommentsPanel";
import ToggleButtonGroup from "../ToggleButtonGroup";
import PostChoiceModal from "../media/PostChoiceModal";
import AuctionConfig from "./AuctionConfig";
import RaffleConfig from "./RaffleConfig";
import ChallengeConfig from "./ChallengeConfig";
import ContestConfig from "./ContestConfig";
import AMAConfig from "./AMAConfig";
import UGCCampaignConfig from "./UGCCampaignConfig";
import ThemedDayConfig from "./ThemedDayConfig";
import CollabTakeoverConfig from "./CollabTakeoverConfig";
import PollQuizConfig from "./PollQuizConfig";
import ConfirmDeleteModal from "../../modals/ConfirmDeleteModal";
import PostAllConfirmModal from "../../modals/PostAllConfirmModal";
import RequestExternalUploadModal from "../../modals/RequestExternalUploadModal";
import MediaEditor from "../media/MediaEditor";
import VideoEditor from "../media/VideoEditor";
import MediaViewer from "../media/MediaViewer";

// ---- helpers kept in sync with EventCard ----
const toAbs = (baseUrl, p) =>
  !p ? p : /^https?:\/\//i.test(p) ? p : `${baseUrl}${p.startsWith("/") ? "" : "/"}${p}`;
const isImage = (f) =>
  (f?.mimetype?.startsWith?.("image/")) ||
  /\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(f?.path || f || "");
const isVideo = (f) =>
  (f?.mimetype?.startsWith?.("video/")) ||
  /\.(mp4|mov|webm|ogg|avi|mkv)$/i.test(f?.path || f || "");

// Video fix props for Safari/iOS
const videoFixProps = {
  playsInline: true,
  preload: "metadata",
  crossOrigin: "anonymous",
  onLoadedMetadata: (e) => {
    try {
      if (e.currentTarget.currentTime === 0) e.currentTarget.currentTime = 0.1;
    } catch {}
  },
};

// ---- layout: match EventAdd ----
const FORM_WIDTH = { maxWidth: 480, margin: "0 auto", padding: "0 12px" };
const SELECT_INNER = {
  width: "100%",
  border: "none",
  outline: "none",
  background: "#3C3C3C",
  color: "#fff",
  minHeight: 120,
  padding: "8px 10px",
};

const EventItemDetail = () => {
  const { t } = useTranslation();
  const [type, setType] = useState("default");
  const [actionType, setActionType] = useState("default");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [topSize, setTopSize] = useState([]);
  const [bottomSize, setBottomSize] = useState([]);
  const [shoeSize, setShoeSize] = useState([]);
  const [, setFiles] = useState([]);
  const [registerStatus, setRegisterStatus] = useState("");
  const [, setImagePath] = useState(null);
  const [imagePaths] = useState([]);
  const [showCamera, setShowCamera] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [, setMessages] = useState([]);
  const [postChoiceOpen, setPostChoiceOpen] = useState(false);
  const [postAllConfirmOpen, setPostAllConfirmOpen] = useState(false);
  const [requestUploadOpen, setRequestUploadOpen] = useState(false);
  const [raffleLink] = useState("");
  
  const [showAuctionConfig, setShowAuctionConfig] = useState(false);
  const [showRaffleConfig, setShowRaffleConfig] = useState(false);
  const [showChallengeConfig, setShowChallengeConfig] = useState(false);
  const [showContestConfig, setShowContestConfig] = useState(false);
  const [showAMAConfig, setShowAMAConfig] = useState(false);
  const [showUGCCampaignConfig, setShowUGCCampaignConfig] = useState(false);
  const [showThemedDayConfig, setShowThemedDayConfig] = useState(false);
  const [showCollabTakeoverConfig, setShowCollabTakeoverConfig] = useState(false);
  const [showPollQuizConfig, setShowPollQuizConfig] = useState(false);

  const [eventItem, setEventItem] = useState(null);
  const [eventItemImages, setEventItemImages] = useState([]);
  const [eventItemVideos, setEventItemVideos] = useState([]);
  const [selectedMedia, setSelectedMedia] = useState({}); // { [url]: true }
  const [editingMedia, setEditingMedia] = useState(null); // { url: string }
  const [mediaViewer, setMediaViewer] = useState(null); // { url, isVideo }
  // Media view mode for the media column: 'single' = one per row, 'grid' = 3 across no spacing
  const [mediaViewMode, setMediaViewMode] = useState(() => {
    try { return localStorage.getItem('os_itemdetail_media_view') || 'single'; } catch { return 'single'; }
  });
  const setMediaViewModeAndPersist = (mode) => {
    setMediaViewMode(mode);
    try { localStorage.setItem('os_itemdetail_media_view', mode); } catch {}
  };

  // Video editor state
  const [editingVideo, setEditingVideo] = useState(null); // { url: string }
  const [videoEdits, setVideoEdits] = useState({}); // { [videoUrl]: { vttUrl, posterUrl, editMetadata } }

  const navigate = useNavigate();
  const location = useLocation();
  const { tenant, eventId, itemId } = useParams();

  const baseUrl =
    process.env.REACT_APP_BASE_URL ||
    (typeof window !== "undefined" ? window.location.origin : "http://localhost:5000");

  // unified media list
  const rawItemFiles = [
    ...(Array.isArray(eventItem?.files) ? eventItem.files : []),
    ...(eventItemImages || []),
    ...(eventItemVideos || []),
  ].filter(Boolean);

  const itemMedia = rawItemFiles
    .map((f) => {
      const path = f?.path || f?.url || f;
      const video = isVideo(f) || isVideo(path);
      return { url: toAbs(baseUrl, path), isVideo: !!video, _raw: f };
    })
    .filter((m, idx, arr) => arr.findIndex((x) => x.url === m.url) === idx);

  // Count selected media
  const selectedCount = Object.values(selectedMedia).filter(Boolean).length;

  // Total media available on this item (images + videos)
  const totalAvailableMedia =
    (eventItemImages?.length || 0) + (eventItemVideos?.length || 0);

  // Click handler for the Post button — gates the empty-selection case behind a confirmation
  const handlePostClick = () => {
    if (selectedCount > 0) {
      setPostChoiceOpen(true);
    } else {
      setPostAllConfirmOpen(true);
    }
  };

  const pantSizes = [
    { category: "M", range: "26 - 27", size: 26, label: "XXS" },
    { category: "M", range: "27 - 28", size: 27, label: "XS" },
    { category: "M", range: "28 - 29", size: 28, label: "XS" },
    { category: "M", range: "29 - 30", size: 29, label: "S" },
    { category: "M", range: "30 - 31", size: 30, label: "S" },
    { category: "M", range: "31 - 32", size: 31, label: "M" },
    { category: "M", range: "32 - 33", size: 32, label: "M" },
    { category: "M", range: "33 - 34", size: 33, label: "M/L" },
    { category: "M", range: "34 - 35", size: 34, label: "L" },
    { category: "M", range: "35 - 36", size: 35, label: "L" },
    { category: "M", range: "36 - 37", size: 36, label: "XL" },
    { category: "M", range: "38 - 39", size: 38, label: "XL" },
    { category: "M", range: "40 - 41", size: 40, label: "XXL" },
    { category: "M", range: "42 - 43", size: 42, label: "XXL" },
    { category: "M", range: "44 - 45", size: 44, label: "XXXL" },
    { category: "M", range: "46 - 47", size: 46, label: "XXXL" },
    { category: "M", range: "48 - 49", size: 48, label: "XXXL" },
    { category: "M", range: "50 - 51", size: 50, label: "4XL" },
    { category: "W", range: "23 - 25", size: "00", altSize: "23/24", label: "XXS" },
    { category: "W", range: "25 - 26", size: "0", altSize: "25", label: "XXS" },
    { category: "W", range: "26 - 27", size: "2", altSize: "26", label: "XS" },
    { category: "W", range: "27 - 28", size: "4", altSize: "27", label: "XS" },
    { category: "W", range: "28 - 29", size: "6", altSize: "28", label: "S" },
    { category: "W", range: "29 - 30", size: "8", altSize: "29", label: "S" },
    { category: "W", range: "30 - 31", size: "10", altSize: "30", label: "M" },
    { category: "W", range: "31 - 32", size: "12", altSize: "31", label: "M" },
    { category: "W", range: "32 - 33", size: "14", altSize: "32", label: "L" },
    { category: "W", range: "33 - 34", size: "16", altSize: "33", label: "L" },
    { category: "W", range: "34 - 35", size: "18", altSize: "34", label: "XL" },
    { category: "W", range: "36 - 37", size: "20", altSize: "36", label: "XL" },
    { category: "W", range: "38 - 39", size: "22", altSize: "38", label: "XXL" },
    { category: "W", range: "40 - 41", size: "24", altSize: "40", label: "XXL" },
  ];

  const canCreateAction = [
    "raffle", "auction", "challenge", "contest",
    "poll_quiz", "ugc_campaign", "ama",
    "collab_takeover", "themed_day",
  ].includes((actionType || "").toLowerCase());

  const actionConfigMap = {
    auction: setShowAuctionConfig,
    raffle: setShowRaffleConfig,
    challenge: setShowChallengeConfig,
    contest: setShowContestConfig,
    ama: setShowAMAConfig,
    ugc_campaign: setShowUGCCampaignConfig,
    themed_day: setShowThemedDayConfig,
    collab_takeover: setShowCollabTakeoverConfig,
    poll_quiz: setShowPollQuizConfig,
  };

  const goToActionSetup = () => {
    const act = (actionType || "").toLowerCase();
    const setter = actionConfigMap[act];
    if (setter) setter(true);
  };

  // Auto-open the matching action config when arriving with ?openAction=<type>
  // (used by NewPost / PostDetail action picker hand-off). Runs once per match.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requested = (params.get("openAction") || "").toLowerCase();
    if (!requested) return;
    const setter = actionConfigMap[requested];
    if (!setter) return;
    setActionType(requested);
    setter(true);
    // Strip the param so refresh / back-nav doesn't reopen the modal.
    params.delete("openAction");
    const next = params.toString();
    navigate(
      { pathname: location.pathname, search: next ? `?${next}` : "" },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Load item + its media
  useEffect(() => {
    const fetchItemDetails = async () => {
      try {
        const [itemRes, imagesRes] = await Promise.all([
          axios.get(`${baseUrl}/api/events/${tenant}/events/${eventId}/items/${itemId}`),
          axios.get(`${baseUrl}/api/events/${tenant}/events/${eventId}/items/${itemId}/images`),
        ]);

        const itemData = itemRes.data.item || {};
        setEventItem(itemData);
        setName(itemData.name || "");
        setDescription(itemData.description || "");
        setType(itemData.type || "default");

        if (itemData.attributes) {
          const top = itemData.attributes.filter((a) => a.attributename === "top_size").map((a) => a.attributevalue);
          const bottom = itemData.attributes.filter((a) => a.attributename === "bottom_size").map((a) => a.attributevalue);
          const shoes = itemData.attributes.filter((a) => a.attributename === "shoe_size").map((a) => a.attributevalue);
          setTopSize(top);
          setBottomSize(bottom);
          setShoeSize(shoes);
        }

        const images = imagesRes.data.images || [];
        setEventItemImages(images);

        let videos = [];
        try {
          const vidsRes = await axios.get(
            `${baseUrl}/api/${tenant}/events/${eventId}/items/${itemId}/videos`
          );
          videos = vidsRes.data.videos || [];
          setEventItemVideos(videos);
        } catch {
          setEventItemVideos([]);
        }

        // Seed selectedMedia from persisted action_associated flags so the
        // checkboxes reflect which media are currently linked to the event
        // action. Each checkbox toggle then updates state, and saving the
        // item persists the new selection back to t_file.action_associated.
        const initialSelected = {};
        [...images, ...videos].forEach((f) => {
          if (f?.action_associated) {
            const path = f?.path || f?.url || f;
            const url = /^https?:\/\//i.test(path) ? path : `${baseUrl}${path}`;
            initialSelected[url] = true;
          }
        });
        setSelectedMedia(initialSelected);
      } catch (error) {
        console.error("Error fetching event item details:", error);
      }
    };
    fetchItemDetails();
  }, [tenant, eventId, itemId, baseUrl]);

  /**
   * Handle native camera capture
   * Upload captured files to event item
   * @param {File[]} capturedFiles - Array of File objects from NativeCameraCapture
   */
  const handleNativeCameraCapture = async (capturedFiles) => {
    console.log('📸 Captured files from native camera:', capturedFiles);
    
    // Close the camera modal
    setShowCamera(false);
    
    if (!capturedFiles?.length || !itemId) {
      console.log('No files captured or no item ID');
      return;
    }

    try {
      setUploading(true);
      
      // Create FormData and append all captured files
      const formData = new FormData();
      formData.append("reftable", "t_event_item");
      formData.append("tableid", itemId);
      capturedFiles.forEach((file) => {
        formData.append("files", file, file.name);
      });

      console.log('📤 Uploading captured files to event item:', itemId);

      // Upload using existing endpoint
      const response = await axios.post(
        `${baseUrl}/api/events/${tenant}/events/${eventId}/items/${itemId}/uploadimage`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log('Upload progress:', percentCompleted + '%');
          }
        }
      );

      console.log('✅ Upload successful:', response.data);

      if (response.data.success) {
        // Reload images/videos for this event item
        const { data } = await axios.get(
          `${baseUrl}/api/events/${tenant}/events/${eventId}/items/${itemId}/images`
        );
        
        const allMedia = data?.images || [];
        setEventItemImages(allMedia.filter(isImage));
        setEventItemVideos(allMedia.filter(isVideo));

        setRegisterStatus(t('events.filesUploadedSuccess'));
        console.log('✅ Event item media reloaded successfully!');
      }

    } catch (error) {
      console.error('❌ Upload failed:', error);

      // Show error to user
      const errorMessage = error?.response?.data?.message ||
                          error?.message ||
                          t('events.uploadMediaFailed');
      setRegisterStatus(`Error: ${errorMessage}`);
    } finally {
      setUploading(false);
    }
  };

  const handleUploadComplete = async (files) => {
    try {
      if (files.length === 0) return;
      setFiles(files);
      setImagePath(URL.createObjectURL(files[0]));
      setIsImageModalOpen(false);

      const formData = new FormData();
      formData.append("reftable", "t_event_item");
      formData.append("tableid", itemId);
      files.forEach((file) => formData.append("files", file));

      try {
        const response = await axios.post(
          `${baseUrl}/api/events/${tenant}/events/${eventId}/items/${itemId}/uploadimage`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        if (response.data.success) {
          const { data } = await axios.get(
            `${baseUrl}/api/events/${tenant}/events/${eventId}/items/${itemId}/images`
          );
          setEventItemImages(data?.images || []);
        }
      } catch (error) {
        console.error("❌ Error uploading images:", error);
      }
      setRegisterStatus(t('events.filesUploadedSuccess'));
    } catch (error) {
      console.error("Error linking uploaded files:", error);
    }
  };

  useEffect(() => {
    document.body.classList.add("eventlist-fullbleed");
    return () => document.body.classList.remove("eventlist-fullbleed");
  }, []);

  // Messages
  useEffect(() => {
    axios
      .get(`${baseUrl}/api/events/${tenant}/events/${eventId}/items/${itemId}/messages`)
      .then((res) => setMessages(Array.isArray(res.data) ? res.data : res.data?.messages || []))
      .catch((err) => console.error("Error loading messages:", err));
  }, [tenant, eventId, itemId, baseUrl]);

  const updateItem = async (e) => {
    e.preventDefault();
    try {
      // Build attributes array from size selections
      const attributes = [];
      if (type === "clothing") {
        topSize.forEach((v) => attributes.push({ attributename: "top_size", attributevalue: v }));
        bottomSize.forEach((v) => attributes.push({ attributename: "bottom_size", attributevalue: v }));
      }
      if (type === "shoes") {
        shoeSize.forEach((v) => attributes.push({ attributename: "shoe_size", attributevalue: v }));
      }

      // Map current checkbox selection → file ids. The server flips
      // action_associated to 1 for ids in this list and 0 for the rest of
      // this item's files. Includes empty array to explicitly clear.
      const associated_file_ids = itemMedia
        .filter((m) => selectedMedia[m.url] && m._raw?.id)
        .map((m) => Number(m._raw.id))
        .filter((id) => Number.isFinite(id));

      await axios.put(
        `${baseUrl}/api/events/${tenant}/events/${eventId}/items/${itemId}`,
        { name, description, type, attributes, associated_file_ids }
      );
      navigate(`/${tenant}/${eventId}/eventDetail`);
    } catch (err) {
      console.error("Failed to update item:", err);
    }
  };

  /**
   * Post ALL media (when no selection)
   */
  const handleCreateMediaPost = () => {
    const toPublic = (p) => (p?.startsWith("/public/") ? p : p?.startsWith("/tenants/") ? `/public${p}` : p);
    const cover = toPublic(eventItemImages?.[0]?.path || "");
    navigate(`/${tenant}/${eventId}/${itemId}/mediaPost`, {
      state: {
        context: "event-item",
        tenant,
        eventId,
        itemId,
        title: name || "",
        description: description || "",
        image: cover,
        images: (eventItemImages || []).map((i) => toPublic(i.path)),
        videos: (eventItemVideos || []).map((v) => toPublic(v.path)),
        item: eventItem,
      },
      replace: false,
    });
  };

  /**
   * Post ONLY selected media
   */
  const handlePostSelected = () => {
    const selectedUrls = Object.keys(selectedMedia).filter((url) => selectedMedia[url]);

    if (selectedUrls.length === 0) {
      alert(t('events.selectMediaToPost'));
      return;
    }

    const toPublic = (p) => (p?.startsWith("/public/") ? p : p?.startsWith("/tenants/") ? `/public${p}` : p);

    const selectedImages = [];
    const selectedVideos = [];

    // Collect selected images
    eventItemImages.forEach((img) => {
      const url = toAbs(baseUrl, img.path || img.url || img);
      if (selectedUrls.includes(url)) {
        selectedImages.push(toPublic(img.path || img.url || img));
      }
    });

    // Collect selected videos
    eventItemVideos.forEach((vid) => {
      const url = toAbs(baseUrl, vid.path || vid.url || vid);
      if (selectedUrls.includes(url)) {
        selectedVideos.push(toPublic(vid.path || vid.url || vid));
      }
    });

    const cover = selectedImages[0] || "";

    navigate(`/${tenant}/${eventId}/${itemId}/mediaPost`, {
      state: {
        context: "event-item",
        tenant,
        eventId,
        itemId,
        title: name || "",
        description: description || "",
        image: cover,
        images: selectedImages,
        videos: selectedVideos,
        item: eventItem,
      },
      replace: false,
    });
  };

  const toggleSelect = (url) =>
    setSelectedMedia((prev) => ({ ...prev, [url]: !prev[url] }));

  /**
   * Handle opening media editor for a specific image
   * @param {string} url - The URL of the image to edit
   */
  const handleEditMedia = (url) => {
    if (!url) return;
    setEditingMedia({ url });
  };

  /**
   * Handle successful save from media editor
   * Replaces the old URL with the new edited URL in the state
   * @param {string} newUrl - The URL of the newly saved edited image
   */
  const handleMediaSaved = async (newUrl) => {
    if (!editingMedia) return;

    const { url: oldUrl } = editingMedia;

    try {
      console.log('✓ Media saved, updating UI:', { oldUrl, newUrl });

      // Update item images state
      setEventItemImages(prev => 
        prev.map(img => 
          toAbs(baseUrl, img.path) === oldUrl 
            ? { ...img, path: newUrl.replace(baseUrl, '') }
            : img
        )
      );

      // If media was selected, update selection to new URL
      if (selectedMedia[oldUrl]) {
        setSelectedMedia(prev => {
          const updated = { ...prev };
          delete updated[oldUrl];
          updated[newUrl] = true;
          return updated;
        });
      }

      // Refresh the item images from server to ensure sync
      try {
        const { data } = await axios.get(
          `${baseUrl}/api/events/${tenant}/events/${eventId}/items/${itemId}/images`
        );
        setEventItemImages(data?.images || []);
        console.log('✓ Refreshed item images from server');
      } catch (err) {
        console.warn('Failed to refresh images:', err);
      }

      // Optionally: delete old file from server
      // Uncomment if you want to automatically delete the original after editing
      /*
      try {
        await axios.post('/api/media/delete-by-path', { 
          path: oldUrl 
        });
        console.log('✓ Old media file deleted');
      } catch (err) {
        console.warn('Failed to delete old media file:', err);
      }
      */
    } catch (err) {
      console.error('Error updating media after edit:', err);
    } finally {
      setEditingMedia(null);
    }
  };
  
  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  /**
   * Delete selected media (batch) - Shows confirmation modal
   */
  const handleDeleteSelected = () => {
    const selectedUrls = Object.keys(selectedMedia).filter((url) => selectedMedia[url]);

    if (selectedUrls.length === 0) {
      alert(t('events.selectMediaToDelete'));
      return;
    }

    // Show confirmation modal instead of window.confirm
    setShowDeleteModal(true);
  };

  /**
   * Perform the actual deletion after confirmation
   */
  const performDelete = async () => {
    const selectedUrls = Object.keys(selectedMedia).filter((url) => selectedMedia[url]);

    try {
      // Call backend API for batch deletion
      const response = await axios.delete(
        `${baseUrl}/api/events/${tenant}/events/${eventId}/items/${itemId}/media`,
        { data: { urls: selectedUrls } }
      );
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to delete media');
      }

      // Update UI state after successful deletion
      setEventItemImages((prev) => 
        (prev || []).filter((x) => !selectedUrls.includes(toAbs(baseUrl, x.path || x.url || x)))
      );
      setEventItemVideos((prev) => 
        (prev || []).filter((x) => !selectedUrls.includes(toAbs(baseUrl, x.path || x.url || x)))
      );

      // Clear selection
      setSelectedMedia({});

    } catch (error) {
      console.error("Error deleting selected media:", error);
      alert(t('events.deleteMediaError', { message: error.message || 'Unknown error' }));
    }
  };

  return (
    <div className="container">
      <h2 className="hdr hdr--primary hdr--lg">{t('events.eventItemDetail')}</h2>

      {/* Show upload progress indicator */}
      {uploading && (
        <div
          style={{
            ...FORM_WIDTH,
            padding: "12px",
            margin: "8px auto",
            background: "#e3f2fd",
            border: "1px solid #2196f3",
            borderRadius: "8px",
            fontSize: "14px",
            fontWeight: "500",
            textAlign: "center",
          }}
        >
          {t('events.uploadingMediaFiles')}
        </div>
      )}

      {/* Desktop inline action bar (replaces footer on desktop) */}
      <div className="desktop-action-bar">
        <button type="button" onClick={() => navigate(-1)}>
          <FaArrowLeft /> {t('common.back')}
        </button>
        <button type="button" onClick={() => setShowCamera(true)} disabled={uploading}>
          <FaCamera /> {t('common.takePhoto')}
        </button>
        <button type="button" onClick={() => setIsImageModalOpen(true)} disabled={uploading}>
          <FaUpload /> {t('events.uploadPhoto')}
        </button>
        <button type="button" onClick={() => setRequestUploadOpen(true)} disabled={uploading}>
          <FaCloudUploadAlt /> {t('events.requestExternalUpload')}
        </button>
        <button type="button" onClick={handlePostClick} disabled={uploading}>
          <FaShare /> {selectedCount > 0 ? t('events.postWithCount', { count: selectedCount }) : t('events.post')}
        </button>
        {selectedCount > 0 && (
          <button type="button" className="btn-danger-action" onClick={handleDeleteSelected} disabled={uploading}>
            <FaTrash /> {t('events.deleteWithCount', { count: selectedCount })}
          </button>
        )}
        {canCreateAction && (
          <button type="button" onClick={goToActionSetup} disabled={uploading}>
            <FaPlus /> {t('common.create')}
          </button>
        )}
        <button type="button" className="btn-primary-action" onClick={updateItem} disabled={uploading}>
          <FaSave /> {t('common.save', 'Save')}
        </button>
      </div>

      {/* Selected media count indicator */}
      {selectedCount > 0 && (
        <div
          style={{
            ...FORM_WIDTH,
            padding: "8px 12px",
            margin: "8px auto",
            background: "#ff6800",
            color: "white",
            borderRadius: "8px",
            fontSize: "14px",
            fontWeight: "600",
          }}
        >
          {t('events.mediaItemsSelected', { count: selectedCount })}
        </div>
      )}

      {/* Desktop two-column: ALL form fields (left) | media gallery (right).
          Media column always on the right side per user request. Single
          column on mobile via the existing .desktop-two-col responsive
          grid rules. */}
      <div className="desktop-two-col event-item-detail__two-col" style={{ gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 3fr)' }}>
        <div className="desktop-two-col__form event-item-detail__fields">

      {/* FORM — name + description only; the rest of the form-like inputs
          live below as plain state-driven controls. Enter key still
          submits via this form. */}
      <form onSubmit={updateItem} style={FORM_WIDTH}>
        <div className="form-group">
          <label htmlFor="name">{t('events.itemNameLabel')}</label>
          <input
            id="name"
            type="text"
            className="form-control"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('events.itemNamePlaceholder')}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">{t('events.itemDescriptionLabel')}</label>
          <textarea
            id="description"
            className="form-control"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('events.itemDescriptionPlaceholder')}
          />
        </div>
      </form>

      {/* Item type + sizes (state-driven; no longer wrapped in the <form>) */}
      <div style={FORM_WIDTH}>
        <div className="toggle-group">
          <label className="block mb-2 font-semibold">{t('events.selectItemType')}</label>
          <ToggleButtonGroup
            options={[
              { value: "default", label: t('events.typeDefault') },
              { value: "clothing", label: t('events.typeClothing') },
              { value: "shoes", label: t('events.typeShoes') },
            ]}
            value={type}
            onChange={setType}
          />
        </div>

        {type === "clothing" && (
          <>
            <div className="form-group">
              <label>{t('events.shirtSize')}</label>
              <div className="form-control" style={{ padding: 0 }}>
                <select
                  multiple
                  value={topSize}
                  onChange={(e) =>
                    setTopSize([...e.target.selectedOptions].map((o) => o.value))
                  }
                  style={SELECT_INNER}
                >
                  {[
                    "Extra Small",
                    "Small",
                    "Medium",
                    "Large",
                    "X-Large",
                    "2X-Large",
                    "3X-Large",
                  ].map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>{t('events.pantSize')}</label>
              <div className="form-control" style={{ padding: 0 }}>
                <select
                  multiple
                  value={bottomSize}
                  onChange={(e) =>
                    setBottomSize([...e.target.selectedOptions].map((o) => o.value))
                  }
                  style={SELECT_INNER}
                >
                  {pantSizes.map((sz, i) => (
                    <option key={i} value={sz.size}>
                      {sz.category} {sz.label} ({sz.range})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </>
        )}

        {type === "shoes" && (
          <div className="form-group">
            <label>{t('events.shoeSize')}</label>
            <div className="form-control" style={{ padding: 0 }}>
              <select
                multiple
                value={shoeSize}
                onChange={(e) =>
                  setShoeSize([...e.target.selectedOptions].map((o) => o.value))
                }
                style={SELECT_INNER}
              >
                {Array.from({ length: 10 }, (_, i) => (i + 6) + 0.5).map((sz) => (
                  <option key={sz} value={sz}>
                    {sz}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {imagePaths.length > 0 && (
          <div
            className="preview-container"
            style={{ display: "flex", flexWrap: "wrap", marginTop: 10 }}
          >
            {imagePaths.map((src, i) => (
              <img
                key={i}
                src={src}
                alt={`Preview ${i}`}
                style={{ width: 100, marginRight: 5, marginBottom: 5 }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Action Type select + Configure/View buttons */}
      <div style={{ ...FORM_WIDTH, marginTop: 12 }}>
        <div className="form-group">
          <label className="block mb-2 font-semibold">{t('events.actionType')}</label>
          <select
            className="form-control"
            style={{ width: '100%' }}
            value={actionType}
            onChange={(e) => setActionType(e.target.value)}
          >
            <option value="default">{t('events.typeDefault')}</option>
            <option value="auction">{t('events.typeAuction')}</option>
            <option value="raffle">{t('events.typeRaffle')}</option>
            <option value="challenge">{t('events.typeChallenge')}</option>
            <option value="contest">{t('events.typeContest')}</option>
            <option value="poll_quiz">{t('events.typePollQuiz')}</option>
            <option value="ugc_campaign">{t('events.typeUGCCampaign')}</option>
            <option value="ama">{t('events.typeAMA')}</option>
            <option value="collab_takeover">{t('events.typeCollabTakeover')}</option>
            <option value="themed_day">{t('events.typeThemedDay')}</option>
          </select>
        </div>

        {canCreateAction && (
          <div className="flex gap-2 mt-3 flex-wrap">
            <button
              type="button"
              className="btn btn-primary"
              onClick={goToActionSetup}
            >
              {t('events.configureAction', { type: t(`events.type${actionType.charAt(0).toUpperCase() + actionType.slice(1).replace(/_([a-z])/g, (_, c) => c.toUpperCase())}`) })}
            </button>
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={() => navigate(`/${tenant}/${eventId}/${itemId}/actionDetail?type=${actionType}`)}
            >
              View Details
            </button>
          </div>
        )}
      </div>

      {/* Request Upload button */}
      <div style={{ ...FORM_WIDTH, marginTop: 12, marginBottom: 8 }}>
        <button
          type="button"
          className="btn btn-outline"
          onClick={() => setRequestUploadOpen(true)}
          disabled={uploading}
          style={{
            width: "100%",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            padding: "10px 14px",
            background: "transparent",
            color: "#fff",
            border: "1px solid rgba(60, 60, 60, 0.55)",
            borderRadius: 8,
            cursor: uploading ? "not-allowed" : "pointer",
            opacity: uploading ? 0.6 : 1,
            fontWeight: 500,
          }}
        >
          <FaCloudUploadAlt /> {t('events.requestExternalUpload')}
        </button>
      </div>

      {/* Comments */}
      <div style={{ ...FORM_WIDTH, marginTop: 4 }}>
        <CommentsPanel
          baseUrl={baseUrl}
          tenantId={tenant}
          eventId={eventId}
          itemId={itemId}
        />
      </div>

      {registerStatus && (
        <p className="status-message" style={FORM_WIDTH}>
          {registerStatus}
        </p>
      )}

        </div>{/* end desktop-two-col__form (left = fields) */}

        <div className="desktop-two-col__media event-item-detail__media">

      {/* Media gallery header with view-mode toggle (parallel with header) */}
      <div className="media-column-header">
        <h3 style={{ margin: 0 }}>{t('events.media')}</h3>
        <div className="media-view-toggle" role="group" aria-label="Media view mode">
          <button
            type="button"
            className={`view-toggle-btn ${mediaViewMode === 'single' ? 'active' : ''}`}
            onClick={() => setMediaViewModeAndPersist('single')}
            aria-label="Single column view"
            title="Single column"
          >
            <FaList />
          </button>
          <button
            type="button"
            className={`view-toggle-btn ${mediaViewMode === 'grid' ? 'active' : ''}`}
            onClick={() => setMediaViewModeAndPersist('grid')}
            aria-label="Grid view"
            title="Grid (3 across)"
          >
            <FaTh />
          </button>
        </div>
      </div>

      {itemMedia.length > 0 ? (
        <div className={`item-media-list item-media-list--${mediaViewMode}`}>
          <div className={`item-media-cards item-media-cards--${mediaViewMode}`}>
          {itemMedia.map((m, idx) => (
            <article
              key={`media-${idx}`}
              className="feed-card"
              style={{
                padding: 0,
                border: "1px solid rgba(0,0,0,0.15)",
                borderRadius: 12,
                overflow: "hidden",
              }}
            >
              {/* Media display — tap to open fullscreen viewer */}
              <div
                style={{ cursor: "pointer" }}
                onClick={() => setMediaViewer({ url: m.url, isVideo: m.isVideo })}
              >
                {m.isVideo ? (
                  <video
                    className="feed-media"
                    src={m.url}
                    preload="metadata"
                    playsInline
                    {...videoFixProps}
                  />
                ) : (
                  <img
                    className="feed-media"
                    src={m.url}
                    alt=""
                    onError={(e) => (e.currentTarget.src = toAbs(baseUrl, "/public/app/default-image.png"))}
                  />
                )}
              </div>

              {/* Controls BELOW the media */}
              {/* <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "8px 12px",
                  // background: "rgba(255,255,255,0.96)",
                  background: "#253866",
                  borderTop: "1px solid rgba(0,0,0,0.08)",
                }}
              >
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={!!selectedMedia[m.url]}
                    onChange={() => toggleSelect(m.url)}
                    style={{ width: 16, height: 16 }}
                  />
                  Select
                </label>

              </div> */}
              {/* Controls BELOW the media */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "8px 12px",
                  background: "#253866",  // Your brand blue
                  borderTop: "1px solid rgba(0,0,0,0.08)",
                }}
              >
                <label className="flex items-center gap-2 text-sm" style={{ color: "#ffffff" }}>
                  <input
                    type="checkbox"
                    checked={!!selectedMedia[m.url]}
                    onChange={() => toggleSelect(m.url)}
                    style={{ width: 16, height: 16 }}
                  />
                  {t('events.select')}
                </label>
                
                <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                  {/* EDIT BUTTON - UPDATED STYLING */}
                  {!m.isVideo && (
                    <button
                      type="button"
                      aria-label="Edit item media"
                      onClick={() => handleEditMedia(m.url)}
                      style={{
                        background: "#3C3C3C",  // Gray as requested
                        color: "#ffffff",
                        border: "none",
                        padding: "6px 8px",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "11px",
                        fontWeight: "500",
                        minWidth: "45px",  // Quarter width, compact
                        whiteSpace: "nowrap"
                      }}
                    >
                      {t('events.edit')}
                    </button>
                  )}
                  {/* EDIT BUTTON - for videos */}
                  {m.isVideo && (
                    <button
                      type="button"
                      aria-label="Edit video"
                      onClick={(e) => { e.stopPropagation(); setEditingVideo({ url: m.url }); }}
                      style={{
                        background: "#3C3C3C",
                        color: "#ffffff",
                        border: "none",
                        padding: "6px 8px",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "11px",
                        fontWeight: "500",
                        minWidth: "45px",
                        whiteSpace: "nowrap"
                      }}
                    >
                      Edit
                    </button>
                  )}
                </div>
              </div>
            </article>
          ))}
          </div>{/* end item-media-cards */}
        </div>
      ) : (
        <p>{t('events.noMediaAvailable')}</p>
      )}

        </div>{/* end desktop-two-col__media (right = media) */}
      </div>{/* end desktop-two-col */}

      {/* Auction/Raffle Config Modals — wrapped in .modal-overlay so they
          render as fixed-position modals on top of the page. overflow:auto
          on the inner panel makes the form scrollable when it exceeds the
          85vh max-height set by .modal-content. */}
      {showAuctionConfig && (
        <div className="modal-overlay" onClick={() => setShowAuctionConfig(false)}>
          <div
            className="modal-content"
            style={{ overflow: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            <AuctionConfig
              tenantId={tenant}
              eventId={eventId}
              itemId={itemId}
              itemName={name}
              onClose={() => setShowAuctionConfig(false)}
            />
          </div>
        </div>
      )}

      {showRaffleConfig && (
        <div className="modal-overlay" onClick={() => setShowRaffleConfig(false)}>
          <div
            className="modal-content raffle-config-modal"
            style={{ overflow: 'auto', maxWidth: 1100, width: '95%' }}
            onClick={(e) => e.stopPropagation()}
          >
            <RaffleConfig
              tenantId={tenant}
              eventId={eventId}
              itemId={itemId}
              itemName={name}
              onClose={() => setShowRaffleConfig(false)}
            />
          </div>
        </div>
      )}

      {showChallengeConfig && (
        <div className="modal-overlay" onClick={() => setShowChallengeConfig(false)}>
          <div
            className="modal-content"
            style={{ overflow: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            <ChallengeConfig
              tenantId={tenant}
              eventId={eventId}
              itemId={itemId}
              itemName={name}
              onClose={() => setShowChallengeConfig(false)}
            />
          </div>
        </div>
      )}

      {showContestConfig && (
        <div className="modal-overlay" onClick={() => setShowContestConfig(false)}>
          <div
            className="modal-content"
            style={{ overflow: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            <ContestConfig
              tenantId={tenant}
              eventId={eventId}
              itemId={itemId}
              itemName={name}
              onClose={() => setShowContestConfig(false)}
            />
          </div>
        </div>
      )}

      {showAMAConfig && (
        <div className="modal-overlay" onClick={() => setShowAMAConfig(false)}>
          <div
            className="modal-content"
            style={{ overflow: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            <AMAConfig
              tenantId={tenant}
              eventId={eventId}
              itemId={itemId}
              itemName={name}
              onClose={() => setShowAMAConfig(false)}
            />
          </div>
        </div>
      )}

      {showUGCCampaignConfig && (
        <div className="modal-overlay" onClick={() => setShowUGCCampaignConfig(false)}>
          <div
            className="modal-content"
            style={{ overflow: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            <UGCCampaignConfig
              tenantId={tenant}
              eventId={eventId}
              itemId={itemId}
              itemName={name}
              onClose={() => setShowUGCCampaignConfig(false)}
            />
          </div>
        </div>
      )}

      {showThemedDayConfig && (
        <div className="modal-overlay" onClick={() => setShowThemedDayConfig(false)}>
          <div
            className="modal-content"
            style={{ overflow: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            <ThemedDayConfig
              tenantId={tenant}
              eventId={eventId}
              itemId={itemId}
              itemName={name}
              onClose={() => setShowThemedDayConfig(false)}
            />
          </div>
        </div>
      )}

      {showCollabTakeoverConfig && (
        <div className="modal-overlay" onClick={() => setShowCollabTakeoverConfig(false)}>
          <div
            className="modal-content"
            style={{ overflow: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            <CollabTakeoverConfig
              tenantId={tenant}
              eventId={eventId}
              itemId={itemId}
              itemName={name}
              onClose={() => setShowCollabTakeoverConfig(false)}
            />
          </div>
        </div>
      )}

      {showPollQuizConfig && (
        <div className="modal-overlay" onClick={() => setShowPollQuizConfig(false)}>
          <div
            className="modal-content"
            style={{ overflow: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            <PollQuizConfig
              tenantId={tenant}
              eventId={eventId}
              itemId={itemId}
              itemName={name}
              onClose={() => setShowPollQuizConfig(false)}
            />
          </div>
        </div>
      )}

      {raffleLink && (
        <div
          className="rounded p-2 mb-2"
          style={{ ...FORM_WIDTH, background: "#1f2937", color: "#fff", marginTop: 12 }}
        >
          <div className="flex items-center justify-between gap-2">
            <span className="truncate">{raffleLink}</span>
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => navigator.clipboard?.writeText?.(raffleLink)}
            >
              {t('events.copyLink')}
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      {/* Native Camera Capture Component */}
      {showCamera && (
        <NativeCameraCapture
          mode="both"              // Allow both photo and video capture
          multiple={true}          // Allow multiple captures
          maxFiles={10}            // Maximum 10 files per session
          maxImageSizeMB={10}      // Max 10MB per image
          maxVideoSizeMB={100}     // Max 100MB per video
          onCapture={handleNativeCameraCapture}
          onClose={() => setShowCamera(false)}
        />
      )}

      {isImageModalOpen && (
        <ImageUploadModal
          tenantId={tenant}
          imageName={`${Date.now()}_${tenant}_${name}`}
          onUploadComplete={handleUploadComplete}
          onClose={() => setIsImageModalOpen(false)}
        />
      )}

      {/* Media Editor Modal */}
      {editingMedia && (
        <MediaEditor
          src={editingMedia.url}
          tenantId={tenant}
          eventId={eventId}
          itemId={itemId}
          onClose={() => setEditingMedia(null)}
          onSaved={handleMediaSaved}
          uploadUrl="/api/media/upload"
        />
      )}

      {/* Video Editor Modal */}
      {editingVideo && (
        <VideoEditor
          src={editingVideo.url}
          tenantId={Number(tenant) || 1}
          eventId={eventId ? Number(eventId) : null}
          itemId={itemId ? Number(itemId) : null}
          existingVttUrl={videoEdits[editingVideo.url]?.vttUrl || null}
          onSaved={(results) => {
            setVideoEdits(prev => ({
              ...prev,
              [editingVideo.url]: {
                vttUrl: results.vttUrl || null,
                posterUrl: results.posterUrl || null,
                editMetadata: results.editMetadata || null,
              },
            }));
            setEditingVideo(null);
          }}
          onClose={() => setEditingVideo(null)}
        />
      )}

      {/* Footer with dynamic buttons based on selection */}
      <AppFooter
        buttons={[
          {
            icon: <FaArrowLeft />,
            label: t('common.back'),
            onClick: () => navigate(-1),
            disabled: uploading,
          },
          {
            icon: <FaCamera />,
            label: t('common.takePhoto'),
            onClick: () => setShowCamera(true),
            disabled: uploading,
          },
          {
            icon: <FaUpload />,
            label: t('events.uploadPhoto'),
            onClick: () => setIsImageModalOpen(true),
            disabled: uploading,
          },
          // Show "Post (X)" when media selected, otherwise "Post"
          ...(selectedCount > 0 ? [{
            icon: <FaShare />,
            label: t('events.postWithCount', { count: selectedCount }),
            onClick: handlePostClick,
            disabled: uploading,
          }] : [{
            icon: <FaShare />,
            label: t('events.post'),
            onClick: handlePostClick,
            disabled: uploading,
          }]),
          // Show "Delete (X)" button when media is selected
          ...(selectedCount > 0 ? [{
            icon: <FaTrash />,
            label: t('events.deleteWithCount', { count: selectedCount }),
            onClick: handleDeleteSelected,
            disabled: uploading,
          }] : []),
          ...(canCreateAction ? [{
            icon: <FaPlus />,
            label: t('common.create'),
            onClick: goToActionSetup,
            disabled: uploading,
          }] : []),
        ]}
      />

      {/* Confirm Delete Modal */}
      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={performDelete}
        count={Object.values(selectedMedia).filter(Boolean).length}
        context="event-item"
      />

      {/* Request External Upload Modal */}
      <RequestExternalUploadModal
        isOpen={requestUploadOpen}
        onClose={async () => {
          setRequestUploadOpen(false);
          // Refresh item media so any externally-uploaded files appear
          try {
            const { data } = await axios.get(
              `${baseUrl}/api/events/${tenant}/events/${eventId}/items/${itemId}/images`
            );
            setEventItemImages(data?.images || []);
          } catch (err) {
            console.warn('Failed to refresh item images after upload:', err);
          }
          try {
            const { data } = await axios.get(
              `${baseUrl}/api/${tenant}/events/${eventId}/items/${itemId}/videos`
            );
            setEventItemVideos(data?.videos || []);
          } catch (err) {
            console.warn('Failed to refresh item videos after upload:', err);
          }
        }}
        tenantId={tenant}
        eventId={eventId}
        itemId={itemId}
        itemName={name}
      />

      {/* Post Choice Modal */}
      {postChoiceOpen && (
        <PostChoiceModal
          isOpen={postChoiceOpen}
          onClose={() => setPostChoiceOpen(false)}
          title={selectedCount > 0 ? t('events.postSelectedMedia') : t('events.eventPost')}
          hint={selectedCount > 0
            ? t('events.postSelectedHint', { count: selectedCount })
            : t('events.postHint')}
          onPostNow={() => {
            setPostChoiceOpen(false);
            // Post selected media if any selected, otherwise post all
            if (selectedCount > 0) {
              handlePostSelected();
            } else {
              handleCreateMediaPost();
            }
          }}
          onSchedule={() => {
            setPostChoiceOpen(false);
            
            if (selectedCount > 0) {
              // Schedule with ONLY selected media
              const selectedUrls = Object.keys(selectedMedia).filter((url) => selectedMedia[url]);
              const toPublic = (p) => (p?.startsWith("/public/") ? p : p?.startsWith("/tenants/") ? `/public${p}` : p);
              
              const selectedImages = [];
              const selectedVideos = [];

              eventItemImages.forEach((img) => {
                const url = toAbs(baseUrl, img.path || img.url || img);
                if (selectedUrls.includes(url)) {
                  selectedImages.push(toPublic(img.path || img.url || img));
                }
              });

              eventItemVideos.forEach((vid) => {
                const url = toAbs(baseUrl, vid.path || vid.url || vid);
                if (selectedUrls.includes(url)) {
                  selectedVideos.push(toPublic(vid.path || vid.url || vid));
                }
              });

              navigate(`/${tenant}/${eventId}/${itemId}/schedulePost`, {
                state: {
                  context: "event-item",
                  tenant,
                  eventId,
                  itemId,
                  title: name || "",
                  description: description || "",
                  image: selectedImages[0] || "",
                  images: selectedImages,
                  videos: selectedVideos,
                  item: eventItem,
                },
              });
            } else {
              // Schedule with ALL media
              const toPublic = (p) => (p?.startsWith("/public/") ? p : p?.startsWith("/tenants/") ? `/public${p}` : p);
              const cover = eventItemImages?.[0]?.path || "";
              
              navigate(`/${tenant}/${eventId}/${itemId}/schedulePost`, {
                state: {
                  context: "event-item",
                  tenant,
                  eventId,
                  itemId,
                  title: name || "",
                  description: description || "",
                  image: toPublic(cover),
                  images: (eventItemImages || []).map((i) => toPublic(i.path)),
                  videos: (eventItemVideos || []).map((v) => toPublic(v.path)),
                  item: eventItem,
                },
              });
            }
          }}
        />
      )}
      {mediaViewer && (
        <MediaViewer
          src={mediaViewer.url}
          isVideo={mediaViewer.isVideo}
          subtitleSrc={mediaViewer.isVideo ? videoEdits[mediaViewer.url]?.vttUrl || null : null}
          onClose={() => setMediaViewer(null)}
        />
      )}

      {/* Post-all confirmation modal — shown when user clicks Post with nothing selected */}
      <PostAllConfirmModal
        isOpen={postAllConfirmOpen}
        onClose={() => setPostAllConfirmOpen(false)}
        onConfirm={() => setPostChoiceOpen(true)}
        totalMedia={totalAvailableMedia}
        context="event-item"
      />
    </div>
  );
};

export default EventItemDetail;
